import type { ShearAnchorPosition } from '@api/fastAPI.schemas'

export type DropKind = 'before' | 'after' | 'onto'

export type Results2ListItem = {
  id: string
  label?: string
  type: string
  qs: string
  /** Third-line caption when using compact list (`hideExportButton` on Results2ElementList). */
  assemblyName?: string
  eta?: number
  etaC?: number
  /** Short label of the check governing η (M, N, Q, …) — shown before the η value. */
  etaShort?: string
  /** Full readable category of the η-governing check — shown as the short label's tooltip. */
  etaShortTooltip?: string
  etaAllowedOverutilizationPercent?: number
  etaCAllowedOverutilizationPercent?: number
  /** If true, hide η column values (utilization not applicable for this item). */
  etaNotApplicable?: boolean
  /**
   * True when the element's η is governed by a failed Schwingung (Vibration) check
   * (binary check whose max_utilization is 1.0). The η cell should render "ERR" instead
   * of a numeric value in this case.
   */
  etaIsVibrationError?: boolean
  /**
   * Extra value shown right of the η stack — anchors put their resulting design tensile load
   * (F_d,neu) there. Pre-formatted, since only some lists have one.
   */
  trailingMetric?: {
    value: string
    unit?: string
    tooltip?: string
    /** The number behind `value` — what the "sort by metric" mode orders on. */
    sortValue: number
    /** Tints the value like an η chip — anchors mark an uncritical design load this way. */
    highlight?: 'success'
  }
  status: 'ok' | 'warn' | 'err' | 'idle'

  ok?: boolean
  /**
   * `MemberPositionBundle.exported` for the row’s bundle (parent + children).
   * Used to initialize the detail “Exportiert” toggle when a child row is selected.
   */
  bundleExported?: boolean
  parent?: string
  /** True when this grouped child’s QS label differs from its parent’s. */
  qsDiffersFromParent?: boolean
  /**
   * The `qs` column holds no real value (anchors: no anchor was chosen). Such rows sort last in
   * the `qs` sort mode, in either direction.
   */
  qsMissing?: boolean
  /** Anchors: the Zuganker in `qs` is set by hand instead of chosen by the prioritization. */
  qsManual?: boolean
  /** Schubanker rows: which end of the wall the row's anchor sits at, for the position filter. */
  shearAnchorPosition?: ShearAnchorPosition
  comment: string
  /** Vorbemerkungen — persisted with position grouping (`MemberPositionBundle.extended_comment`). */
  extended_comment: string
}

export type SortMode =
  | 'eta'
  | 'etaC'
  | 'trailingMetric'
  | 'alpha'
  | 'crossSection'
  | 'anchor'
  | 'tree'

export type SortDir = 'asc' | 'desc'

/** Direction a sort mode starts in when first selected (before the user toggles it). */
export const DEFAULT_SORT_DIR: Record<SortMode, SortDir> = {
  eta: 'desc',
  etaC: 'desc',
  trailingMetric: 'desc',
  alpha: 'asc',
  crossSection: 'asc',
  anchor: 'asc',
  tree: 'desc',
}

/**
 * Sort modes a list offers, in the order they are shown. A list that carries
 * something else in its second column passes its own set: the Schubanker tab
 * has no cross section and no η c,90, but it does have a chosen anchor.
 */
export const DEFAULT_SORT_MODES: SortMode[] = [
  'eta',
  'etaC',
  'trailingMetric',
  'alpha',
  'crossSection',
  'tree',
]

/** One row of the virtualized list — a list item plus its position in the tree/flat layout. */
export type RenderedListRow = {
  item: Results2ListItem
  depth: number
  isParent: boolean
}
